import { Text } from '@mantine/core'
import { useMediaQuery, useDisclosure } from '@mantine/hooks'
import clsx from 'clsx'
import ClaireCard from '../../stories/ClaireCard/ClaireCard'
import ProgressBox from '../../widgets/components/ProgressBox'
import KpiList from '../../widgets/components/KpiList'
import PerformanceSection from '../../common/components/metrics/PerformanceSection'
import classes from './PlayerComponents.module.css'
import { Typography } from '../../common/typography'
import { useCompetitionId } from '../../common/hooks'
import { useMonthKPIs } from '../../widgets/hooks'
import { getInsight } from '../../performance/insights'
import {
  WidgetContextProvider,
  useWidgetContext,
} from '../../widgets/providers'
import { usePlayerRanks } from '../../ranks/api'
import Stat from '../../stories/Stat/Stat'
import { IconStar } from '@tabler/icons-react'
import { Player } from '../api'
import Breakdown from '../../stories/Breakdown/Breakdown'
import { useState } from 'react'
import { useLearningStatsOptional } from '../../learning/api'
import LearningLevel from '../../learning/components/LearningLevel'
import { useActiveCompetitions } from '../../navigation/api'

export default function PlayerDetails({ player }: { player: Player }) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const ctx = useWidgetContext()
  const [
    pointBreakdownOpen,
    { open: openPointBreakdown, close: closePointBreakdown },
  ] = useDisclosure(false)
  const competition_id = useCompetitionId()
  const { data: activeCompetitionsData } = useActiveCompetitions()
  const hasLearningModule =
    activeCompetitionsData?.platform_features.has_learning_module ?? false
  const fields = player.fields as Record<string, string | number | boolean>
  const hasCountry = fields && 'country' in fields
  const hasPosition = fields && 'position' in fields
  const [subject, setSubject] = useState<'rating' | 'total-points'>('rating')

  const { data: playerRanks } = usePlayerRanks({
    competition_id: competition_id,
    player_id: player.player_id,
    page: 1,
    page_size: 1,
  })

  const { data: playerLearningStats } = useLearningStatsOptional({
    player_id: player.player_id,
    enabled: hasLearningModule,
  })

  function handleStatClick(subject: 'rating' | 'total-points') {
    setSubject(subject)
    openPointBreakdown()
  }

  if (playerRanks.items.length === 0) {
    return (
      <div className={classes.noDataContainer}>
        <Typography
          fs="md"
          fc={{ light: 'gray-8', dark: 'gray-2' }}
          ta="center"
        >
          Currently there is no data available for player:
        </Typography>
        <Typography
          fs="md"
          fw={700}
          fc={{ light: 'gray-8', dark: 'gray-2' }}
          ta="center"
        >{`${player.name}`}</Typography>
      </div>
    )
  }

  const playerRank = playerRanks.items[0]

  const { data: month } = useMonthKPIs(competition_id, true, {
    subject_type: 'user',
    subject_ids: ctx.subject?.toString(),
  })
  const insightResult = getInsight(month, false)

  return (
    <div
      className={clsx(classes.player_details_wrapper, {
        [classes.withTopMargin]: (hasCountry || hasPosition) && !isDesktop,
      })}
    >
      <div className={classes.level_stats_row} data-mobile={!isDesktop}>
        <div className={classes.stats_widgets_wrapper}>
          <Stat
            title="Overall Rank"
            value={playerRank?.rank ?? null}
            isValueSub
            background="white"
          />
          <Stat
            title="Rating"
            value={playerRank?.rating ? playerRank.rating.toFixed(2) : null}
            icon={IconStar}
            background="white"
            onClick={() => handleStatClick('rating')}
          />
          <Stat
            title="Team Rank"
            value={playerRank?.team_rank ?? null}
            isValueSub
            background="white"
          />
          <Stat
            title="Total Points"
            value={playerRank?.points ?? null}
            background="white"
            onClick={() => handleStatClick('total-points')}
          />
        </div>
        {playerLearningStats && (
          <LearningLevel level={playerLearningStats.xp_level} />
        )}
      </div>
      <div
        className={clsx(
          { [classes.player_widgets_wrapper]: isDesktop },
          { [classes.player_widgets_wrapper_mobile]: !isDesktop }
        )}
      >
        <div className={classes.player_stats}>
          <div className={classes.wrapper} style={{ display: 'none' }}>
            <div className={classes.wrapper}>
              <Text component="h6" className={classes.title}>
                Motto
              </Text>
              <Text component="p" className={classes.motto} lineClamp={3}>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Praesent hendrerit purus...
              </Text>
            </div>
          </div>
          <div className={classes.wrapper}>
            <Typography fw={500} lh="130">
              YTD Stats
            </Typography>
            <KpiList id="kpis" />
          </div>
        </div>
        <div className={classes.clair_stats_container}>
          <ClaireCard title="Insights" text={insightResult.text} />
          <div className={classes.stat_cards}>
            <WidgetContextProvider
              context={{ metric: insightResult.topPerformer?.key }}
            >
              <ProgressBox id="insights0" />
            </WidgetContextProvider>
            <WidgetContextProvider
              context={{ metric: insightResult.bottomPerformer?.key }}
            >
              <ProgressBox id="insights1" />
            </WidgetContextProvider>
          </div>
        </div>
      </div>
      <PerformanceSection />
      <Breakdown
        opened={pointBreakdownOpen}
        close={closePointBreakdown}
        competitionId={competition_id}
        playerId={player.player_id}
        subject={subject}
      />
    </div>
  )
}
